import { X, XCircle, Globe, Phone, Monitor, Cpu } from "lucide-react";
import SectionCard from "./SectionCard";
import BarChart from "./charts/BarChart";
import DonutChart from "./charts/DonutChart";
import { formatNum } from "@/lib/constants";
import { cn } from "@/lib/utils";
import LeyendaEstado from "./pipeline/LeyendaEstado";

const CANAL_ICONS = {
  "Portal Paciente": Globe,
  "Recepción": Monitor,
  "API (integraciones)": Cpu,
  "Call Center": Phone,
};

export default function CancelacionesDrawer({ data, periodo, onClose }) {
  if (!data) return null;

  const tasaCancelacion = data.pct_canceladas;
  const citasNoCanceladas = data.total_citas - data.canceladas;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cancelaciones-drawer">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="sticky top-0 z-10 p-5 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-rose-500 flex items-center justify-center text-white">
                <XCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Detalle Cancelaciones</h3>
                <p className="text-xs text-slate-500">PRM · {periodo}</p>
              </div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition"
                    aria-label="Cerrar panel">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          <div className="mt-2">
            <LeyendaEstado estado={data.origen} />
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* KPIs principales */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Citas canceladas</div>
              <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.canceladas)}</div>
              <div className="text-sm text-rose-600 font-medium tabular-nums">{tasaCancelacion}% del total</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pacientes que cancelaron</div>
              <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">{formatNum(data.pacientes_cancelaron)}</div>
              <div className="text-sm text-slate-500 tabular-nums">de {formatNum(data.total_citas)} citas</div>
            </div>
          </div>

          {/* Distribución por canal */}
          <SectionCard title="¿Quién cancela?" subtitle="Distribución por canal de cancelación">
            <DonutChart
              labels={data.por_canal.map((c) => c.canal)}
              series={data.por_canal.map((c) => c.citas)}
              colors={data.por_canal.map((c) => c.color)}
              height={280}
            />
          </SectionCard>

          {/* Detalle por canal */}
          <SectionCard title="Detalle por canal" subtitle="Citas canceladas y participación">
            <div className="space-y-1">
              {data.por_canal.map((c) => {
                const Icon = CANAL_ICONS[c.canal] || Globe;
                return (
                  <div key={c.canal} className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: c.color + "20" }}>
                        <Icon className="w-4 h-4" style={{ color: c.color }} />
                      </div>
                      <span className="text-sm text-slate-700">{c.canal}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-slate-900 tabular-nums">{formatNum(c.citas)}</span>
                      <span className="text-xs text-slate-500 ml-2 tabular-nums">({c.pct}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Barras horizontales por canal */}
          <SectionCard title="Volumen por canal" subtitle="Comparativa visual">
            <BarChart
              categories={data.por_canal.map((c) => c.canal)}
              series={[{ name: "Cancelaciones", data: data.por_canal.map((c) => c.citas) }]}
              colors={data.por_canal.map((c) => c.color)}
              height={200}
              horizontal
              distributed
            />
          </SectionCard>

          {/* Contexto */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Metodología</div>
            <p className="text-sm text-slate-700">
              <strong>Fuente:</strong> {data.fuente}
            </p>
            <p className="text-sm text-slate-700">
              <strong>Definición:</strong> Una cita se considera cancelada cuando su <code className="text-xs bg-slate-100 px-1 rounded">statusfinal = 2</code> en PRM.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Canal:</strong> El campo <code className="text-xs bg-slate-100 px-1 rounded">channelcancel</code> indica quién ejecutó la cancelación: el paciente (Portal), recepción, API automática, o Call Center.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Nota:</strong> Un paciente puede cancelar múltiples citas. Los {formatNum(data.pacientes_cancelaron)} pacientes únicos generaron {formatNum(data.canceladas)} cancelaciones ({(data.canceladas / data.pacientes_cancelaron).toFixed(1)} cancelaciones/paciente).
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
